/**
 * E002-T035: HistoryPanel Component (Run-Level Logging)
 * E002-T046: Backend Logs - Single Source of Truth
 *
 * Displays execution history for an automation block:
 * - List of recent executions (per Run, not per Row)
 * - Input/Process/Output stages
 * - Statistics summary
 *
 * SINGLE SOURCE OF TRUTH:
 * - All logs are stored in PostgreSQL (backend)
 * - Active log (running) comes from LoggingService (in-memory, until persisted)
 * - No more BlockModel storage
 */

import { Button, useConfirmModal } from '@affine/component';
import { workspaceApiFetch } from '@affine/core/modules/cloud';
import { useI18n } from '@affine/i18n';
import { DeleteIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { WorkspaceService } from '../../../workspace';
import { AutomationService } from '../../services/automation';
import {
  type ExecutionLog,
  hasActiveLongRunningOps,
  LoggingService,
  type LongRunningOpInfo,
  type WorkflowNodeStep,
} from '../../services/logging';
import { workflowWorkspaceRoutes } from '../../services/workflow-workspace-routes';
import { formatSttBatchError } from '../../workflow-viewer/stt-batch-error';
import * as styles from '../automation-panel.css';
import { LongRunningOpRow } from './history-row-variants/long-running-op-row';
import { DataRow, ErrorBox } from './history-row-variants/row-primitives';
import {
  formatHistoryErrorMessage,
  formatHistoryExecutionStatusLabel,
  formatHistoryProcessStatusLabel,
} from './history-status-labels';

interface HistoryPanelProps {
  /** Block ID to show history for */
  blockId: string;
}

function historyForegroundPolicy(workspaceId: string, key: string) {
  return {
    workspaceId,
    category: 'foreground-action' as const,
    key: `history:${key}`,
    mode: 'foreground' as const,
    priority: 'foreground' as const,
  };
}

// =============================================================================
// Utility Functions
// =============================================================================

const formatTimestamp = (timestamp: number, justNowLabel: string): string => {
  const date = new Date(timestamp);
  const now = new Date();
  const diff = now.getTime() - timestamp;

  if (diff < 60000) return justNowLabel;
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;

  if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatDuration = (
  startedAt: number,
  completedAt?: number
): string | null => {
  if (!completedAt) return null;

  const duration = completedAt - startedAt;

  if (duration < 1000) return `${duration}ms`;
  if (duration < 60000) return `${(duration / 1000).toFixed(1)}s`;

  const minutes = Math.floor(duration / 60000);
  const seconds = Math.floor((duration % 60000) / 1000);
  return `${minutes}m ${seconds}s`;
};

const formatMediaProgressParts = (
  progress: NonNullable<ExecutionLog['process']>['mediaProgress']
): string[] => {
  if (!progress) return [];

  return [
    progress.message ?? progress.activity ?? null,
    progress.percent !== undefined ? `${progress.percent}%` : null,
    progress.bytesTotal !== undefined
      ? `${formatBytes(progress.bytesDone ?? 0)} / ${formatBytes(
          progress.bytesTotal
        )}`
      : null,
    progress.bitrate ?? null,
    progress.fps !== undefined ? `${progress.fps.toFixed(1)} fps` : null,
    progress.timeRemainingMs !== undefined
      ? `ETA ${Math.max(0, Math.ceil(progress.timeRemainingMs / 1000))}s`
      : null,
  ].filter((part): part is string => Boolean(part));
};

const formatBytes = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex++;
  }
  return `${value >= 10 || unitIndex === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[unitIndex]}`;
};

const normalizeStepName = (value: string | undefined): string =>
  value?.trim().toLowerCase().replace(/\s+/g, ' ') ?? '';

const isProgressForStep = (
  step: WorkflowNodeStep,
  progress: NonNullable<ExecutionLog['process']>['mediaProgress']
): boolean => {
  if (!progress) return false;
  if (step.nodeId && progress.stepId && step.nodeId === progress.stepId) {
    return true;
  }

  const stepName = normalizeStepName(step.name);
  const progressName = normalizeStepName(progress.stepName);
  return Boolean(stepName && progressName && stepName === progressName);
};

const hasStepForProgress = (
  steps: WorkflowNodeStep[] | undefined,
  progress: NonNullable<ExecutionLog['process']>['mediaProgress']
): boolean =>
  Boolean(progress && steps?.some(step => isProgressForStep(step, progress)));

// =============================================================================
// Sub-Components
// =============================================================================

/**
 * sl-trm: StatusBadge now considers rowsWritten for completed status
 * - completed + rowsWritten > 0 = green (success)
 * - completed + rowsWritten === 0 = orange (warning - no output)
 * sl-1ahs.7.5: removed isTestMode branch — execution modes are now only
 * 'execute' | 'trigger', both of which write output.
 */
const StatusBadge: FC<{
  status: ExecutionLog['status'];
  rowsWritten?: number;
  queued?: boolean;
}> = ({ status, rowsWritten, queued }) => {
  const t = useI18n();
  // Determine if this is a "completed but empty" case
  const isCompletedEmpty = status === 'completed' && rowsWritten === 0;

  const colors: Record<ExecutionLog['status'] | 'stopped', string> = {
    running: queued
      ? 'var(--streamlain-warning-color)'
      : 'var(--streamlain-primary-color)',
    // sl-67kc.3.3: parked at external provider — same hue as running
    // to signal "still alive" but distinct label so the user can tell
    // the log is waiting on a callback, not on engine progress.
    awaiting_external: 'var(--streamlain-warning-color)',
    completed: isCompletedEmpty
      ? 'var(--streamlain-warning-color)'
      : 'var(--streamlain-success-color)',
    failed: 'var(--streamlain-error-color)',
    stopped: 'var(--streamlain-text-secondary-color)',
    cancelled: 'var(--streamlain-text-secondary-color)',
  };

  const displayText = queued
    ? t['com.streamlain.streamlain.history.queued']()
    : formatHistoryExecutionStatusLabel(t, status, queued);

  return (
    <span
      style={{
        padding: '2px 6px',
        fontSize: '10px',
        fontWeight: 600,
        borderRadius: '4px',
        backgroundColor: colors[status],
        color: 'white',
        textTransform: 'uppercase',
      }}
    >
      {displayText}
    </span>
  );
};

const StageSection: FC<{
  title: string;
  icon: string;
  children: React.ReactNode;
}> = ({ title, icon, children }) => (
  <div
    style={{
      marginTop: '12px',
      padding: '8px',
      backgroundColor: 'var(--streamlain-background-primary-color)',
      borderRadius: '6px',
    }}
  >
    <div
      style={{
        fontSize: '11px',
        fontWeight: 600,
        marginBottom: '8px',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
      }}
    >
      <span>{icon}</span>
      <span>{title}</span>
    </div>
    {children}
  </div>
);

const JsonPreview: FC<{ data: unknown; maxHeight?: string }> = ({
  data,
  maxHeight = 'none',
}) => (
  <pre
    style={{
      fontSize: '10px',
      fontFamily: 'monospace',
      backgroundColor: 'var(--streamlain-background-secondary-color)',
      padding: '6px',
      borderRadius: '4px',
      overflow: 'auto',
      maxHeight,
      margin: '4px 0 0 0',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-all',
    }}
  >
    {JSON.stringify(data, null, 2)}
  </pre>
);

// =============================================================================
// NodeStepEntry Component - Expandable node step with Input/Output tabs
// =============================================================================

interface NodeStepEntryProps {
  step: WorkflowNodeStep;
  isExpanded: boolean;
  onToggle: () => void;
}

const NodeStepEntry: FC<NodeStepEntryProps> = ({
  step,
  isExpanded,
  onToggle,
}) => {
  const t = useI18n();
  const isRunning = step.status === 'running';
  const progressParts = formatMediaProgressParts(step.mediaProgress);
  const progressMessage =
    step.mediaProgress?.message ?? step.mediaProgress?.activity;
  const progressDetails = progressParts
    .filter(part => part !== progressMessage)
    .join(' · ');

  return (
    <div
      style={{
        backgroundColor: 'var(--streamlain-background-tertiary-color)',
        borderRadius: '6px',
        border: '1px solid var(--streamlain-border-color)',
      }}
    >
      {/* Header - clickable */}
      <div
        onClick={onToggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 10px',
          cursor: 'pointer',
        }}
      >
        {isRunning ? (
          <span className={styles.historyStepSpinner} />
        ) : (
          <span
            style={{
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              backgroundColor:
                step.status === 'success'
                  ? 'var(--streamlain-success-color)'
                  : 'var(--streamlain-error-color)',
              flexShrink: 0,
            }}
          />
        )}
        <span style={{ flex: 1, fontWeight: 500, fontSize: '11px' }}>
          {step.name}
        </span>
        <span style={{ fontSize: '10px', opacity: 0.6 }}>
          {isRunning
            ? t['com.streamlain.streamlain.history.running']()
            : `${step.duration}ms`}
        </span>
      </div>

      {step.mediaProgress && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '3px',
            minWidth: 0,
            padding: '0 10px 8px 24px',
            color: 'var(--streamlain-text-secondary-color)',
            fontSize: '10px',
          }}
        >
          {progressMessage && (
            <span
              style={{
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {progressMessage}
            </span>
          )}
          {progressDetails && (
            <span
              style={{
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {progressDetails}
            </span>
          )}
        </div>
      )}

      {/* Expanded Content — field summaries + step I/O data */}
      {isExpanded && (
        <div
          style={{
            padding: '6px 10px 10px',
            borderTop: '1px solid var(--streamlain-border-color)',
            fontSize: '10px',
          }}
        >
          {/* sl-j7kp.3.2: Step Input Data (if available) or fallback to summary */}
          {step.inputData && step.inputData.length > 0 ? (
            <div style={{ marginBottom: '6px' }}>
              <span style={{ opacity: 0.6, fontWeight: 500 }}>
                {t['com.streamlain.streamlain.history.input-label']()}
              </span>
              <JsonPreview data={step.inputData} maxHeight="200px" />
            </div>
          ) : step.inputSummary && step.inputSummary.length > 0 ? (
            <div style={{ marginBottom: '4px' }}>
              <span style={{ opacity: 0.6 }}>
                {t['com.streamlain.streamlain.history.input-label']()}
              </span>
              {step.inputSummary.join(', ')}
            </div>
          ) : null}

          {/* sl-j7kp.3.2: Step Output Data (if available) or fallback to summary */}
          {step.outputData && step.outputData.length > 0 ? (
            <div style={{ marginBottom: '6px' }}>
              <span style={{ opacity: 0.6, fontWeight: 500 }}>
                {t['com.streamlain.streamlain.history.output-label']()}
              </span>
              <JsonPreview data={step.outputData} maxHeight="200px" />
            </div>
          ) : step.outputSummary && step.outputSummary.length > 0 ? (
            <div style={{ marginBottom: '4px' }}>
              <span style={{ opacity: 0.6 }}>
                {t['com.streamlain.streamlain.history.output-label']()}
              </span>
              {step.outputSummary.join(', ')}
            </div>
          ) : null}

          {/* Error — prefer localised message for known errorCodes */}
          {(() => {
            const translated = formatSttBatchError(
              t,
              step.errorCode,
              step.errorDetails
            );
            const message =
              translated ?? (step.error ? formatHistoryErrorMessage(t) : null);
            return message ? <ErrorBox message={message} size="small" /> : null;
          })()}
        </div>
      )}
    </div>
  );
};

// =============================================================================
// LongRunningOp Sub-Components (sl-67kc.3.3)
// =============================================================================
//
// The `LongRunningOpRow` variant + its helpers live in
// `./history-row-variants/long-running-op-row.tsx`. The split was
// introduced by sl-67kc.3.3 so every long-running-op kind (stt today,
// video-transcode + bulk-ai later) can land its own row variant
// without bloating this panel further.

// =============================================================================
// LogEntry Component
// =============================================================================

const LogEntry: FC<{
  log: ExecutionLog;
  isExpanded: boolean;
  onToggle: () => void;
  onDelete: () => void;
  onCancelOp: (op: LongRunningOpInfo) => Promise<void>;
  onCancelExecution: (executionId: string) => Promise<void>;
  onRetryOp: (op: LongRunningOpInfo) => Promise<void>;
  onDownloadOp: (op: LongRunningOpInfo) => Promise<void>;
  isActive?: boolean;
  rowRef?: (el: HTMLDivElement | null) => void;
}> = ({
  log,
  isExpanded,
  onToggle,
  onDelete,
  onCancelOp,
  onCancelExecution,
  onRetryOp,
  onDownloadOp,
  isActive,
  rowRef,
}) => {
  const t = useI18n();
  const [expandedStepIndex, setExpandedStepIndex] = useState<number | null>(
    null
  );
  // E018-S002: Visual feedback for copy button
  const [copied, setCopied] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  // sl-67kc.3.3: Delete-guard — block delete while any LRO is still
  // active so we never orphan a parked SuspendedExecution row.
  const deleteBlocked = hasActiveLongRunningOps(log);
  const canCancelExecution = log.status === 'running';
  const isQueuedRun =
    log.status === 'running' && log.process?.status === 'queued';

  const handleCancelExecution = useCallback(
    async (e: React.MouseEvent): Promise<void> => {
      e.stopPropagation();
      if (canceling || !canCancelExecution) return;
      setCanceling(true);
      setCancelError(null);
      try {
        await onCancelExecution(log.id);
      } catch (err) {
        setCancelError(err instanceof Error ? err.message : String(err));
      } finally {
        setCanceling(false);
      }
    },
    [canCancelExecution, canceling, log.id, onCancelExecution]
  );

  return (
    <div
      ref={rowRef}
      style={{
        backgroundColor: 'var(--streamlain-background-secondary-color)',
        borderRadius: '8px',
        border: isActive
          ? '2px solid var(--streamlain-primary-color)'
          : '2px solid transparent',
        flexShrink: 0,
      }}
    >
      {/* Header */}
      <div
        onClick={onToggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '10px 12px',
          cursor: 'pointer',
        }}
      >
        <span style={{ fontSize: '12px', opacity: 0.6 }}>
          {/* sl-1ahs.7.5: mode-test branch removed — only 'execute'|'trigger' runs exist */}
          {t['com.streamlain.streamlain.history.mode-run']()}
        </span>
        <StatusBadge
          status={log.status}
          rowsWritten={log.output?.rowsWritten}
          queued={isQueuedRun}
        />
        <span style={{ flex: 1 }} />
        {canCancelExecution && (
          <button
            onClick={e => {
              void handleCancelExecution(e);
            }}
            disabled={canceling}
            aria-label="Stop running execution"
            title={canceling ? 'Stopping execution' : 'Stop running execution'}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '24px',
              height: '24px',
              padding: 0,
              border: 'none',
              borderRadius: '999px',
              backgroundColor: 'var(--streamlain-error-color)',
              color: 'white',
              cursor: canceling ? 'default' : 'pointer',
              opacity: canceling ? 0.6 : 1,
              flexShrink: 0,
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: '10px',
                height: '10px',
                borderRadius: '1px',
                backgroundColor: 'white',
              }}
            />
          </button>
        )}
        <span style={{ fontSize: '11px', opacity: 0.6 }}>
          {formatTimestamp(
            log.startedAt,
            t['com.streamlain.streamlain.history.just-now']()
          )}
        </span>
        <span
          style={{
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
            opacity: 0.4,
          }}
        >
          ▼
        </span>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div
          style={{
            padding: '0 12px 12px',
            borderTop: '1px solid var(--streamlain-border-color)',
          }}
        >
          {/* Duration + Copy Button */}
          <div
            style={{
              padding: '12px 0 0',
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <span style={{ opacity: 0.6 }}>
                {t['com.streamlain.streamlain.history.duration']()}
              </span>
              <span>
                {formatDuration(log.startedAt, log.completedAt) ??
                  t['com.streamlain.streamlain.history.running']()}
              </span>
            </div>
            <button
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                const runData = {
                  id: log.id,
                  mode: log.mode,
                  status: log.status,
                  startedAt: log.startedAt,
                  completedAt: log.completedAt,
                  input: log.input,
                  process: log.process,
                  output: log.output,
                  error: log.error,
                };
                navigator.clipboard
                  .writeText(JSON.stringify(runData, null, 2))
                  .then(() => {
                    setCopied(true);
                    setTimeout(() => setCopied(false), 1500);
                  })
                  .catch(() => {});
              }}
              style={{
                padding: '4px 8px',
                fontSize: '11px',
                border: '1px solid var(--streamlain-border-color)',
                borderRadius: '4px',
                backgroundColor: copied
                  ? 'var(--streamlain-success-color)'
                  : 'transparent',
                cursor: 'pointer',
                color: copied
                  ? 'white'
                  : 'var(--streamlain-text-secondary-color)',
                transition: 'all 0.2s ease',
              }}
            >
              {copied
                ? t['com.streamlain.streamlain.history.copied']()
                : t['com.streamlain.streamlain.history.copy-run']()}
            </button>
          </div>

          {/* sl-67kc.3.3: External long-running ops (STT today). */}
          {log.longRunningOps && log.longRunningOps.length > 0 && (
            <StageSection
              title={t['com.streamlain.streamlain.history.lro.section-title']()}
              icon=""
            >
              {log.longRunningOps.map(op => (
                <LongRunningOpRow
                  key={op.suspendedExecutionId}
                  op={op}
                  onCancel={() => onCancelOp(op)}
                  onRetry={() => onRetryOp(op)}
                  onDownload={() => onDownloadOp(op)}
                />
              ))}
            </StageSection>
          )}

          {/* INPUT Stage */}
          {log.input && (
            <StageSection
              title={t['com.streamlain.streamlain.history.stage-input']()}
              icon=""
            >
              <DataRow
                label={t['com.streamlain.streamlain.history.source-table']()}
                value={log.input.sourceTable}
              />
              <DataRow
                label={t['com.streamlain.streamlain.history.total-rows']()}
                value={log.input.totalRows}
              />
              <DataRow
                label={t['com.streamlain.streamlain.history.passed-filter']()}
                value={log.input.passedFilter}
                color="var(--streamlain-success-color)"
              />
              <DataRow
                label={t['com.streamlain.streamlain.history.filtered-out']()}
                value={log.input.filteredOut}
                color={
                  log.input.filteredOut > 0
                    ? 'var(--streamlain-warning-color)'
                    : undefined
                }
              />
              {log.input.filterConfig && (
                <>
                  <div
                    style={{
                      fontSize: '10px',
                      opacity: 0.6,
                      marginTop: '8px',
                    }}
                  >
                    {t['com.streamlain.streamlain.history.filter-config']()}
                  </div>
                  <JsonPreview data={log.input.filterConfig} />
                </>
              )}
              {log.input.selectedColumns &&
                log.input.selectedColumns.length > 0 && (
                  <>
                    <div
                      style={{
                        fontSize: '10px',
                        opacity: 0.6,
                        marginTop: '8px',
                      }}
                    >
                      {t[
                        'com.streamlain.streamlain.history.selected-columns'
                      ]()}
                    </div>
                    <JsonPreview data={log.input.selectedColumns} />
                  </>
                )}
              {log.input.payloads.length > 0 && (
                <>
                  <div
                    style={{
                      fontSize: '10px',
                      opacity: 0.6,
                      marginTop: '8px',
                    }}
                  >
                    {t['com.streamlain.streamlain.history.payloads']({
                      count: String(log.input.payloads.length),
                    })}
                  </div>
                  <JsonPreview data={log.input.payloads} />
                </>
              )}
            </StageSection>
          )}

          {/* PROCESS Stage */}
          {log.process && (
            <StageSection
              title={t['com.streamlain.streamlain.history.stage-process']()}
              icon=""
            >
              <DataRow
                label={t['com.streamlain.streamlain.history.duration']()}
                value={`${log.process.duration}ms`}
              />
              <DataRow
                label={t['com.streamlain.streamlain.history.status']()}
                value={formatHistoryProcessStatusLabel(t, log.process.status)}
                color={
                  log.process.status === 'success'
                    ? 'var(--streamlain-success-color)'
                    : log.process.status === 'running'
                      ? 'var(--streamlain-primary-color)'
                      : log.process.status === 'queued'
                        ? 'var(--streamlain-warning-color)'
                        : 'var(--streamlain-error-color)'
                }
              />
              {log.process.mediaProgress &&
                !hasStepForProgress(
                  log.process.nodeSteps,
                  log.process.mediaProgress
                ) && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginTop: '12px',
                      padding: '8px 10px',
                      borderRadius: '6px',
                      backgroundColor:
                        'var(--streamlain-background-tertiary-color)',
                      border: '1px solid var(--streamlain-border-color)',
                      fontSize: '11px',
                    }}
                  >
                    <span className={styles.historyStepSpinner} />
                    <span
                      style={{
                        flex: 1,
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontWeight: 500,
                      }}
                    >
                      {log.process.mediaProgress.stepName ??
                        t['com.streamlain.streamlain.history.running']()}
                    </span>
                    <span
                      style={{
                        color: 'var(--streamlain-primary-color)',
                        fontVariantNumeric: 'tabular-nums',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {formatMediaProgressParts(log.process.mediaProgress).join(
                        ' · '
                      ) || t['com.streamlain.streamlain.history.running']()}
                    </span>
                  </div>
                )}
              {log.process.runtimeEvents &&
                log.process.runtimeEvents.length > 0 && (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                      marginTop: '8px',
                      fontSize: '10px',
                      color: 'var(--streamlain-text-secondary-color)',
                    }}
                  >
                    {log.process.runtimeEvents.slice(-5).map(event => (
                      <div
                        key={`${event.timestamp}-${event.stepId ?? ''}-${
                          event.activity ?? event.phase ?? ''
                        }`}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: '8px',
                        }}
                      >
                        <span
                          style={{
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {event.message ??
                            event.activity ??
                            event.stepName ??
                            t['com.streamlain.streamlain.history.running']()}
                        </span>
                        {event.bytesTotal !== undefined ? (
                          <span style={{ whiteSpace: 'nowrap' }}>
                            {formatBytes(event.bytesDone ?? 0)} /{' '}
                            {formatBytes(event.bytesTotal)}
                          </span>
                        ) : event.percent !== undefined ? (
                          <span style={{ whiteSpace: 'nowrap' }}>
                            {event.percent}%
                          </span>
                        ) : null}
                      </div>
                    ))}
                  </div>
                )}
              {log.process.nodeSteps && log.process.nodeSteps.length > 0 && (
                <>
                  <div
                    style={{
                      fontSize: '10px',
                      opacity: 0.6,
                      marginTop: '12px',
                    }}
                  >
                    {t['com.streamlain.streamlain.history.steps']({
                      count: String(log.process.nodeSteps.length),
                    })}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '6px',
                      marginTop: '8px',
                    }}
                  >
                    {log.process.nodeSteps.map((step, i) => (
                      <NodeStepEntry
                        key={i}
                        step={step}
                        isExpanded={expandedStepIndex === i}
                        onToggle={() =>
                          setExpandedStepIndex(
                            expandedStepIndex === i ? null : i
                          )
                        }
                      />
                    ))}
                  </div>
                </>
              )}
              {log.process.error && (
                <ErrorBox message={formatHistoryErrorMessage(t)} size="small" />
              )}
            </StageSection>
          )}

          {/* OUTPUT Stage */}
          {log.output && (
            <StageSection
              title={t['com.streamlain.streamlain.history.stage-output']()}
              icon=""
            >
              <DataRow
                label={t['com.streamlain.streamlain.history.mode']()}
                value={log.output.mode}
              />
              {log.output.targetTable && (
                <DataRow
                  label={t['com.streamlain.streamlain.history.target-table']()}
                  value={log.output.targetTable}
                />
              )}
              <DataRow
                label={t['com.streamlain.streamlain.history.rows-written']()}
                value={log.output.rowsWritten}
                color="var(--streamlain-success-color)"
              />
              {log.output.rowsFailed > 0 && (
                <DataRow
                  label={t['com.streamlain.streamlain.history.rows-failed']()}
                  value={log.output.rowsFailed}
                  color="var(--streamlain-error-color)"
                />
              )}
              {log.output.outputMappings &&
                log.output.outputMappings.length > 0 && (
                  <>
                    <div
                      style={{
                        fontSize: '10px',
                        opacity: 0.6,
                        marginTop: '8px',
                      }}
                    >
                      {t['com.streamlain.streamlain.history.output-mappings']()}
                    </div>
                    <JsonPreview data={log.output.outputMappings} />
                  </>
                )}
            </StageSection>
          )}

          {/* Global Error */}
          {log.terminalReason && (
            <ErrorBox
              message={
                log.terminalReason === 'cycle_blocked'
                  ? t['com.streamlain.streamlain.history.cycle-blocked']()
                  : t[
                      'com.streamlain.streamlain.history.external-loop-suspected'
                    ]()
              }
              size="medium"
            />
          )}
          {log.error && (
            <ErrorBox message={formatHistoryErrorMessage(t)} size="medium" />
          )}
          {cancelError && <ErrorBox message={cancelError} size="small" />}

          {/* Delete Button (only for terminal logs, not active/running) */}
          {/* sl-67kc.3.3: also blocked while any long-running op is still
              parked so the suspension row + STT job rows are not orphaned. */}
          {!isActive && !canCancelExecution && (
            <Button
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                if (deleteBlocked) return;
                onDelete();
              }}
              disabled={deleteBlocked}
              title={
                deleteBlocked
                  ? t['com.streamlain.streamlain.history.lro.delete-blocked']()
                  : undefined
              }
              style={{ marginTop: '12px', width: '100%' }}
            >
              <DeleteIcon style={{ marginRight: '4px' }} />
              {t['com.streamlain.streamlain.history.delete-log']()}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

// =============================================================================
// Main Component
// =============================================================================

export const HistoryPanel: FC<HistoryPanelProps> = ({ blockId }) => {
  const t = useI18n();
  const loggingService = useService(LoggingService);
  const workspaceService = useService(WorkspaceService);
  const automationService = useService(AutomationService);
  const workspaceId = workspaceService.workspace.id;
  const { openConfirmModal } = useConfirmModal();
  const pendingRowTarget = useLiveData(
    automationService.pendingHistoryRowTarget$
  );

  // Active log from LoggingService (in-memory, for running executions)
  const activeLog = useLiveData(loggingService.getActiveLog$(blockId));

  // Logs from LoggingService (backend logs loaded via loadFromBackend)
  const backendLogs = useLiveData(loggingService.getLogs$(blockId));
  // sl-67kc.3.4 REWORK round 3: track when the initial backend fetch
  // settled (success OR error) so the chip deep-link scroll effect can
  // wait for logs to populate before consuming `pendingHistoryRowTarget$`.
  //
  // Round 4 (Codex cross-block race): `automation-panel.tsx:1710` reuses
  // the same HistoryPanel instance across block switches, so a stale
  // `.finally()` from the PREVIOUS block's fetch could fire AFTER
  // `blockId` changed and flip the gate open for the new block before
  // its own fetch settled. The effect cleanup now sets a cancelled
  // flag the promise chain reads, so only the current block's fetch
  // can transition `backendLoaded` to `true`.
  const [backendLoaded, setBackendLoaded] = useState(false);
  useEffect(() => {
    setBackendLoaded(false);
    let cancelled = false;
    loggingService
      .loadFromBackend(blockId, workspaceId)
      .catch(console.error)
      .finally(() => {
        if (cancelled) return;
        setBackendLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [blockId, workspaceId, loggingService]);

  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);
  const rowRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());

  const handleToggle = useCallback((logId: string) => {
    setExpandedLogId(current => (current === logId ? null : logId));
  }, []);

  // Delete a single log via backend API
  const handleDelete = useCallback(
    (logId: string): void => {
      // Update UI immediately
      loggingService.deleteLog(blockId, logId);
      if (expandedLogId === logId) {
        setExpandedLogId(null);
      }

      // Delete from backend asynchronously
      workspaceApiFetch(
        workflowWorkspaceRoutes.log(workspaceId, logId),
        {
          method: 'DELETE',
        },
        historyForegroundPolicy(workspaceId, `delete-log:${logId}`)
      )
        .then(response => {
          if (!response.ok) {
            console.error(
              '[HistoryPanel] Backend delete failed:',
              response.status
            );
            // Refresh to restore if backend failed
            return loggingService.refreshFromBackend(blockId, workspaceId);
          }
          return undefined;
        })
        .catch(err => {
          console.error('[HistoryPanel] Failed to delete log:', err);
          // Refresh to restore on error
          loggingService
            .refreshFromBackend(blockId, workspaceId)
            .catch(console.error);
        });
    },
    [blockId, expandedLogId, loggingService, workspaceId]
  );

  // sl-67kc.3.3: Cancel a long-running operation. Returns from the REST
  // call as 202 (cancel queued). The History panel refreshes once Pub/
  // Sub flips the suspension status — but we also fire a delayed refresh
  // here so the user sees the new status without waiting for an
  // unrelated Socket.IO event.
  const issueLroAction = useCallback(
    async (
      url: string,
      failureLabel: string,
      currentJobId: string
    ): Promise<void> => {
      const response = await workspaceApiFetch(
        url,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        },
        historyForegroundPolicy(workspaceId, `lro:${currentJobId}`)
      );
      if (!response.ok) {
        throw new Error(
          `${failureLabel} (jobId=${currentJobId}, status=${response.status})`
        );
      }
      // give the worker a beat then pull fresh state
      await new Promise(r => setTimeout(r, 800));
      await loggingService.refreshFromBackend(blockId, workspaceId);
    },
    [blockId, loggingService, workspaceId]
  );

  const handleCancelOp = useCallback(
    async (op: LongRunningOpInfo): Promise<void> => {
      await issueLroAction(
        `/api/stt/jobs/${encodeURIComponent(op.jobId)}/cancel`,
        t['com.streamlain.streamlain.history.lro.cancel-failed'](),
        op.jobId
      );
    },
    [issueLroAction, t]
  );

  const handleCancelExecution = useCallback(
    async (executionId: string): Promise<void> => {
      const response = await workspaceApiFetch(
        '/graphql',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            query:
              'mutation CancelExecution($executionId: String!) { cancelExecution(executionId: $executionId) }',
            variables: { executionId },
          }),
        },
        historyForegroundPolicy(workspaceId, `cancel-execution:${executionId}`)
      );
      if (!response.ok) {
        throw new Error(`Cancel failed (status=${response.status})`);
      }

      const payload = (await response.json()) as {
        data?: { cancelExecution?: boolean };
        errors?: Array<{ message?: string }>;
      };
      if (payload.errors && payload.errors.length > 0) {
        throw new Error(
          payload.errors.map(error => error.message ?? 'Unknown').join('; ')
        );
      }
      if (payload.data?.cancelExecution !== true) {
        throw new Error('No running execution was found to cancel.');
      }

      // Pod termination is asynchronous; refresh once the dispatcher has had
      // time to persist the terminal cancelled state.
      await new Promise(r => setTimeout(r, 800));
      await loggingService.refreshFromBackend(blockId, workspaceId);
    },
    [blockId, loggingService, workspaceId]
  );

  const handleRetryOp = useCallback(
    async (op: LongRunningOpInfo): Promise<void> => {
      await issueLroAction(
        `/api/stt/jobs/${encodeURIComponent(op.jobId)}/retry-resume`,
        t['com.streamlain.streamlain.history.lro.retry-failed'](),
        op.jobId
      );
    },
    [issueLroAction, t]
  );

  // sl-67kc.3.3 REWORK: Download-Transcript reads the cached provider
  // result buffered on `SuspendedExecution.resumePayload`. We fetch
  // JSON, wrap it in a Blob, and trigger a browser download so the
  // user can inspect or archive the transcript without a copy-paste
  // step. Errors surface on the row's `actionError` state via the
  // thrown rejection (the LongRunningOpRow handle() catch path).
  const handleDownloadOp = useCallback(
    async (op: LongRunningOpInfo): Promise<void> => {
      const response = await workspaceApiFetch(
        `/api/stt/jobs/${encodeURIComponent(op.jobId)}/transcript`,
        { method: 'GET', headers: { Accept: 'application/json' } },
        {
          workspaceId,
          category: 'workspace-api',
          key: `stt-transcript:${op.jobId}`,
          mode: 'background',
          priority: 'visible',
        }
      );
      if (!response.ok) {
        throw new Error(
          `${t['com.streamlain.streamlain.history.lro.download-failed']()} (jobId=${op.jobId}, status=${response.status})`
        );
      }
      const body = await response.blob();
      const href = URL.createObjectURL(body);
      try {
        const anchor = document.createElement('a');
        anchor.href = href;
        anchor.download = `stt-transcript-${op.jobId}.json`;
        document.body.append(anchor);
        anchor.click();
        anchor.remove();
      } finally {
        URL.revokeObjectURL(href);
      }
    },
    [t, workspaceId]
  );

  // Clear all logs via backend API (with confirmation)
  const handleClearAll = useCallback((): void => {
    openConfirmModal({
      title: 'Clear execution history?',
      description:
        'All execution logs for this automation will be permanently deleted. This action cannot be undone.',
      confirmText: t['com.streamlain.streamlain.history.clear-all'](),
      confirmButtonOptions: {
        variant: 'error',
      },
      onConfirm: () => {
        // Clear local state immediately for instant UI feedback
        loggingService.clearLogs(blockId);
        setExpandedLogId(null);

        // Then delete from backend asynchronously
        workspaceApiFetch(
          workflowWorkspaceRoutes.blockLogCollection(workspaceId, blockId),
          {
            method: 'DELETE',
          },
          historyForegroundPolicy(workspaceId, `clear-logs:${blockId}`)
        )
          .then(response => {
            if (!response.ok) {
              console.error(
                '[HistoryPanel] Backend delete failed:',
                response.status
              );
              // Refresh to restore if backend failed
              return loggingService.refreshFromBackend(blockId, workspaceId);
            }
            return undefined;
          })
          .catch(err => {
            console.error('[HistoryPanel] Failed to clear logs:', err);
            // Refresh to restore on error
            loggingService
              .refreshFromBackend(blockId, workspaceId)
              .catch(console.error);
          });
      },
    });
  }, [blockId, loggingService, openConfirmModal, t, workspaceId]);

  // Combine backend logs with active log (if running)
  const allLogs = useMemo(() => {
    const logsById = new Map<string, ExecutionLog>();

    for (const log of backendLogs) {
      logsById.set(log.id, log);
    }

    if (activeLog) {
      const existing = logsById.get(activeLog.id);
      logsById.set(
        activeLog.id,
        existing ? { ...existing, ...activeLog } : activeLog
      );
    }

    return Array.from(logsById.values()).sort(
      (a, b) => b.startedAt - a.startedAt
    );
  }, [backendLogs, activeLog]);

  // sl-67kc.3.4 REWORK round 3: the workspace operations chip deep-link
  // activates the target block on AutomationService and publishes the
  // target via `pendingHistoryRowTarget$`. This panel is block-scoped
  // per `automation-panel.tsx:1710`, so the effect only matters for the
  // HistoryPanel whose blockId matches the target.
  //
  // Timing gate (Codex round 3 F1): we must NOT consume the target until
  //   (a) `loadFromBackend()` settles (tracked by `backendLoaded`), AND
  //   (b) the target log is actually in `allLogs` so the `rowRef`
  //       callback has a LogEntry to attach to.
  // Otherwise first-open deep-links raced the network fetch — consume
  // cleared the target, `rowRefs.current.get(targetId)` was undefined,
  // and the scroll silently no-op'd.
  //
  // Once backendLoaded is true and the target is STILL missing from
  // `allLogs` (log was deleted, or the target was for a different
  // block that was already consumed upstream), we consume anyway to
  // drop the stale target — otherwise it would sit in LiveData forever.
  useEffect(() => {
    if (!pendingRowTarget || pendingRowTarget.blockId !== blockId) return;
    if (!backendLoaded) return;
    const targetId = pendingRowTarget.executionLogId;
    const targetExists = allLogs.some(l => l.id === targetId);
    automationService.consumePendingHistoryRowTarget(blockId);
    if (!targetExists) return;
    setExpandedLogId(targetId);
    // setExpandedLogId queues a render; the LogEntry's rowRef callback
    // fires during that commit. requestAnimationFrame runs after
    // commit, so the lookup sees the freshly-attached DOM node.
    requestAnimationFrame(() => {
      rowRefs.current
        .get(targetId)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [pendingRowTarget, blockId, automationService, allLogs, backendLoaded]);

  // Calculate stats from all logs
  const stats = useMemo(() => {
    if (allLogs.length === 0) {
      return {
        totalExecutions: 0,
        totalRowsProcessed: 0,
        successRate: 0,
      };
    }

    const completedLogs = allLogs.filter(
      (l: ExecutionLog) => l.status === 'completed'
    );
    const totalRowsProcessed = allLogs.reduce(
      (sum: number, l: ExecutionLog) => sum + (l.input?.passedFilter ?? 0),
      0
    );

    return {
      totalExecutions: allLogs.length,
      totalRowsProcessed,
      successRate:
        allLogs.length > 0
          ? Math.round((completedLogs.length / allLogs.length) * 100)
          : 0,
      lastExecution: allLogs[0]?.startedAt,
    };
  }, [allLogs]);

  return (
    <div
      className={styles.section}
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        overflow: 'hidden',
      }}
    >
      {/* Stats Summary with Clear All Button */}
      {stats.totalExecutions > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 12px',
            backgroundColor: 'var(--streamlain-background-secondary-color)',
            borderRadius: '8px',
            marginBottom: '12px',
            fontSize: '12px',
          }}
        >
          <div style={{ display: 'flex', gap: '16px', flex: 1 }}>
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}
            >
              <span style={{ opacity: 0.6, fontSize: '10px' }}>
                {t['com.streamlain.streamlain.history.runs']()}
              </span>
              <span style={{ fontWeight: 600 }}>{stats.totalExecutions}</span>
            </div>
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}
            >
              <span style={{ opacity: 0.6, fontSize: '10px' }}>
                {t['com.streamlain.streamlain.history.rows']()}
              </span>
              <span style={{ fontWeight: 600 }}>
                {stats.totalRowsProcessed}
              </span>
            </div>
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}
            >
              <span style={{ opacity: 0.6, fontSize: '10px' }}>
                {t['com.streamlain.streamlain.history.success']()}
              </span>
              <span
                style={{
                  fontWeight: 600,
                  color:
                    stats.successRate >= 80
                      ? 'var(--streamlain-success-color)'
                      : stats.successRate >= 50
                        ? 'var(--streamlain-warning-color)'
                        : 'var(--streamlain-error-color)',
                }}
              >
                {stats.successRate}%
              </span>
            </div>
          </div>
          <button
            onClick={handleClearAll}
            title={t['com.streamlain.streamlain.history.clear-all']()}
            style={{
              padding: '4px',
              fontSize: '10px',
              backgroundColor: 'transparent',
              border: '1px solid var(--streamlain-border-color)',
              borderRadius: '4px',
              cursor: 'pointer',
              color: 'var(--streamlain-text-secondary-color)',
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            <DeleteIcon style={{ width: '12px', height: '12px' }} />
          </button>
        </div>
      )}

      {/* Log List */}
      {allLogs.length === 0 ? (
        <div
          style={{
            padding: '24px',
            textAlign: 'center',
            fontSize: '13px',
            opacity: 0.6,
          }}
        >
          {t['com.streamlain.streamlain.history.empty']()}
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            flex: 1,
            overflowY: 'auto',
            minHeight: 0,
          }}
        >
          {allLogs.map((log: ExecutionLog) => (
            <LogEntry
              key={log.id}
              log={log}
              isExpanded={expandedLogId === log.id}
              onToggle={() => handleToggle(log.id)}
              onDelete={() => handleDelete(log.id)}
              onCancelOp={handleCancelOp}
              onCancelExecution={handleCancelExecution}
              onRetryOp={handleRetryOp}
              onDownloadOp={handleDownloadOp}
              isActive={activeLog?.id === log.id}
              rowRef={el => {
                if (el) rowRefs.current.set(log.id, el);
                else rowRefs.current.delete(log.id);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};
